import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { BudgetParams, BudgetProductType } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetProductsStore, createBudgetProductsStore } from './budgetProductsStore';

type BudgetProps = {
  children: React.ReactNode;
};

export type { ProductsMap } from './budgetProductsStore';

export type Budget = {
  budgetStatus: string;
  budgetStatusSet: (arg: string) => void;
  date: string;
  dateSet: (arg: string) => void;
  params?: BudgetParams;
  refreshHeader: () => void;
  setIsQuantityUpdating: (arg: boolean) => void;
  incrementQuantityUpdate: () => void;
  decrementQuantityUpdate: () => void;
  endDate: string;
  endDateSet: (arg: string) => void;
  isEndDatePassed: boolean;
  // Products live in a store; rows read them with useBudgetProduct(productId)
  productsStore: BudgetProductsStore;
  setProducts: (products: BudgetProductType[]) => void;
  addProducts: (products: BudgetProductType[]) => void;
  updateColorQuantity: (productId: string, colorId: string, quantity: number) => void;
  getProduct: (productId: string) => BudgetProductType | undefined;
};

const defaultState: Budget = {
  params: undefined,
  date: '',
  dateSet: () => null,
  budgetStatus: '',
  budgetStatusSet: () => null,
  refreshHeader: () => null,
  setIsQuantityUpdating: () => null,
  incrementQuantityUpdate: () => null,
  decrementQuantityUpdate: () => null,
  endDate: '',
  endDateSet: () => null,
  isEndDatePassed: false,
  // Products defaults
  productsStore: createBudgetProductsStore(),
  setProducts: () => null,
  addProducts: () => null,
  updateColorQuantity: () => null,
  getProduct: () => undefined,
};

export const BudgetContext = createContext(defaultState);

/**
 * Values that change on every quantity save (header refresh counter, "saving"
 * flag). Kept out of BudgetContext so they re-render the header only, not
 * every product row.
 */
type BudgetHeaderSignals = { headerRefreshTrigger: number; isQuantityUpdating: boolean };

export const BudgetHeaderSignalsContext = createContext<BudgetHeaderSignals>({
  headerRefreshTrigger: 0,
  isQuantityUpdating: false,
});

/** One product of the budget page; re-renders the caller only when that product changes. */
export function useBudgetProduct(productId: string): BudgetProductType | undefined {
  const { productsStore } = useContext(BudgetContext);
  return useSyncExternalStore(productsStore.subscribe, () => productsStore.get(productId));
}

const HEADER_REFRESH_DEBOUNCE_MS = 400;

export const BudgetProvider: React.FunctionComponent<BudgetProps> = ({ children }) => {
  const { id, gender } = useParams();
  const location = useLocation();
  const locationState = location.state as { type?: string } | undefined;

  const [budgetStatus, budgetStatusSet] = useState<string>('');
  const [date, dateSet] = useState<string>('');
  const [headerRefreshTrigger, headerRefreshTriggerSet] = useState<number>(0);
  const [quantityUpdateCount, setQuantityUpdateCount] = useState<number>(0);
  const isQuantityUpdating = quantityUpdateCount > 0;
  const [endDate, endDateSet] = useState<string>('');
  const productsStore = useMemo(() => createBudgetProductsStore(), []);
  const [budgetType, budgetTypeSet] = useState<string>(locationState?.type || '');

  const isEndDatePassed = useMemo(() => {
    if (!endDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse date in DD/MM/YYYY format
    // Expected format: "14/12/2025 00:00:00"
    let endDateObj: Date;
    if (endDate.includes('/')) {
      const [datePart] = endDate.split(' ');
      const [day, month, year] = datePart.split('/');
      // Create date with month-1 since JS months are 0-indexed
      endDateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    } else {
      endDateObj = new Date(endDate);
    }
    endDateObj.setHours(0, 0, 0, 0);

    // Consider the budget as passed if endDate is before today (not including today)
    return endDateObj < today;
  }, [endDate]);

  const { season, zone, marketLine, warehouse } = useAppSelector((state) => state.settings);

  // Stable between renders so the context value only changes when its data does
  const params = useMemo(
    () => ({
      season,
      zone,
      seasonLine: marketLine,
      warehouse: warehouse.code,
      budgetNum: id!,
      genderCode: gender!,
      type: budgetType,
    }),
    [season, zone, marketLine, warehouse.code, id, gender, budgetType]
  );

  // Quantity saves call this one by one; a burst of saves re-fetches the header once.
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>();
  const refreshHeader = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(
      () => headerRefreshTriggerSet((prev) => prev + 1),
      HEADER_REFRESH_DEBOUNCE_MS
    );
  }, []);
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Fetch budget type from budget list if not available via navigation state
  useEffect(() => {
    if (!budgetType && season && marketLine && zone && id) {
      budgetDataService.getAll(season, marketLine, zone).then((res) => {
        const found = res.data.find(
          (b: { num: number; type?: string; genderCode?: string }) =>
            String(b.num) === String(id) && (!gender || b.genderCode === gender)
        );
        if (found?.type) {
          budgetTypeSet(found.type);
        }
      });
    }
  }, [budgetType, season, marketLine, zone, id, gender]);

  const incrementQuantityUpdate = useCallback(() => {
    setQuantityUpdateCount((prev) => prev + 1);
  }, []);

  const decrementQuantityUpdate = useCallback(() => {
    setQuantityUpdateCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Deprecated: kept for compatibility but implemented using counter
  const setIsQuantityUpdating = useCallback(
    (value: boolean) => {
      if (value) incrementQuantityUpdate();
      else decrementQuantityUpdate();
    },
    [incrementQuantityUpdate, decrementQuantityUpdate]
  );

  const state: Budget = useMemo(
    () => ({
      params,
      budgetStatus,
      budgetStatusSet,
      date,
      dateSet,
      refreshHeader,
      setIsQuantityUpdating,
      incrementQuantityUpdate,
      decrementQuantityUpdate,
      endDate,
      endDateSet,
      isEndDatePassed,
      productsStore,
      setProducts: productsStore.setProducts,
      addProducts: productsStore.addProducts,
      updateColorQuantity: productsStore.updateColorQuantity,
      getProduct: productsStore.get,
    }),
    [
      params,
      budgetStatus,
      date,
      refreshHeader,
      setIsQuantityUpdating,
      incrementQuantityUpdate,
      decrementQuantityUpdate,
      endDate,
      isEndDatePassed,
      productsStore,
    ]
  );

  const headerSignals = useMemo(
    () => ({ headerRefreshTrigger, isQuantityUpdating }),
    [headerRefreshTrigger, isQuantityUpdating]
  );

  return (
    <BudgetContext.Provider value={state}>
      <BudgetHeaderSignalsContext.Provider value={headerSignals}>
        {children}
      </BudgetHeaderSignalsContext.Provider>
    </BudgetContext.Provider>
  );
};
